import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligenceV2Service, serviceName } from '../service';
import { GetServiceQualityStatsIO, ServiceQueries } from '../types';
import { useCallIntelligenceV2Queries } from './use-call-intelligence-v2-queries';
import { UseCallIntelligenceV2QueryEndpointArgs, useCallIntelligenceV2Query } from './use-call-intelligence-v2-query';

/**
 * A hook that returns a query for the `GetServiceQualityStats` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetServiceQualityStatsQuery = <E = unknown, D = GetServiceQualityStatsIO['output']>(
  args: UseCallIntelligenceV2QueryEndpointArgs<'GetServiceQualityStats', E, D>
) =>
  useCallIntelligenceV2Query({
    endpointName: 'GetServiceQualityStats',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetServiceQualityStats` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetServiceQualityStatsQueries = <E = unknown, D = GetServiceQualityStatsIO['output']>(
  argsArr: UseCallIntelligenceV2QueryEndpointArgs<'GetServiceQualityStats', E, D>[]
) =>
  useCallIntelligenceV2Queries(
    argsArr.map((args) => ({
      endpointName: 'GetServiceQualityStats',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetServiceQualityStats` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetServiceQualityStats = () => {
  const queryClient = useQueryClient();

  const fetchGetServiceQualityStats = async <E = unknown, D = GetServiceQualityStatsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallIntelligenceV2QueryEndpointArgs<'GetServiceQualityStats', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetServiceQualityStatsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetServiceQualityStats'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetServiceQualityStatsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetServiceQualityStats'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetServiceQualityStats'>({
          serviceName,
          endpointName: 'GetServiceQualityStats',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligenceV2Service.GetServiceQualityStats(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetServiceQualityStats;
};
